/**
 * PurchasesList — Factures Fournisseurs (v2, refonte "Pennylane/Axonaut").
 *
 * Design senior SaaS :
 *  - Bandeau 4 KPIs cliquables (encours / retard / à valider / à échoir 7j)
 *  - Barre de filtres intelligente (recherche, statut multi, fournisseur,
 *    période, catégorie PCG, montants, retard, doublons, récurrents)
 *  - Table dense enrichie (badge OCR, récurrent, retard, doublon, concentration)
 *  - Actions batch (delete drafts, validation multiple, normalize, rename)
 *  - Pagination serveur 25/50/100
 *  - Tri sur toutes les colonnes
 *
 * Les notes de frais ont été retirées de cette page — elles ont leur propre
 * entrée "Notes de frais" dans le sidebar Achats.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { DatePickerFR } from "@/components/ui/date-picker-fr";
import {
  Plus, FileText, ArrowUpRight, Trash2, RefreshCw, FilePenLine,
  Search, X, AlertTriangle, Clock, Hourglass, Wallet, Sparkles,
  ChevronDown, ChevronUp, Filter, Copy, Repeat, ScanLine,
  CheckCircle2, ArrowUp, ArrowDown, Building2, CalendarDays,
  Upload, Plug, User, Layers,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import PdfPreviewPopover from "@/components/PdfPreviewPopover";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  } catch { return iso; }
}
function fmtEuro(n, opts = {}) {
  const s = (Number(n) || 0).toLocaleString("fr-FR", {
    minimumFractionDigits: opts.digits ?? 2,
    maximumFractionDigits: opts.digits ?? 2,
  });
  return `${s} €`;
}
function fmtEuroCompact(n) {
  return fmtEuro(Number(n) || 0, { digits: 0 });
}
function daysBetween(iso) {
  if (!iso) return null;
  try {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const d = new Date(iso);
    d.setHours(0, 0, 0, 0);
    return Math.round((d - now) / 86400000);
  } catch { return null; }
}

const STATUS_META = {
  to_validate:    { label: "À valider",       cls: "bg-amber-50 text-amber-800 border-amber-200" },
  validated:      { label: "Validée",         cls: "bg-blue-50 text-blue-700 border-blue-200" },
  imported:       { label: "Importée",        cls: "bg-sky-50 text-sky-700 border-sky-200" },
  partially_paid: { label: "Part. payée",     cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  paid:           { label: "Payée",           cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected:       { label: "Refusée",         cls: "bg-red-50 text-red-700 border-red-200" },
  draft:          { label: "Brouillon",       cls: "bg-slate-100 text-slate-700 border-slate-200" },
  cancelled:      { label: "Annulée",         cls: "bg-slate-100 text-slate-500 border-slate-200" },
};

// Badge dédié à l'état de PAIEMENT (indépendant du statut workflow) — pour
// que l'utilisateur voie tout de suite si une facture est réglée ou non,
// même quand le statut workflow est "Importée" ou "Validée".
// N'apparaît PAS quand le statut workflow indique déjà l'état paiement
// (paid / partially_paid) — évite le doublon "Payée / Payée".
function paymentMeta(row) {
  const total = Number(row.total_ttc) || 0;
  const paid = Number(row.paid_amount_ttc) || 0;
  const remaining = Number(row.remaining_ttc != null
    ? row.remaining_ttc
    : total - paid);
  // Ne pas afficher pour les statuts qui parlent déjà de paiement,
  // ni pour les factures rejetées / annulées / brouillon
  if (["paid", "partially_paid", "rejected", "cancelled", "draft"].includes(row.status)) return null;
  if (total <= 0) return null;
  if (paid >= total - 0.01 || remaining <= 0.01) {
    // Cas rare : total réglé mais workflow pas encore à jour
    return { label: "Payée", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  }
  if (paid > 0.01) {
    return { label: "Part. payée", cls: "bg-amber-50 text-amber-800 border-amber-200" };
  }
  return { label: "Non payée", cls: "bg-red-50 text-red-700 border-red-200" };
}

const SOURCE_META = {
  manual:       { label: "Manuel",     short: "Manuel",   cls: "bg-slate-50 text-slate-700 border-slate-200",  icon: "user" },
  ocr:          { label: "OCR IA",     short: "OCR",      cls: "bg-violet-50 text-violet-700 border-violet-100", icon: "scan" },
  import_batch: { label: "Import lot", short: "Lot",      cls: "bg-violet-50 text-violet-700 border-violet-100", icon: "upload" },
  recurrence:   { label: "Récurrence", short: "Récur.",   cls: "bg-indigo-50 text-indigo-700 border-indigo-100", icon: "repeat" },
  axonaut:      { label: "Axonaut",    short: "Axonaut",  cls: "bg-blue-50 text-blue-700 border-blue-100",       icon: "plug" },
  pennylane:    { label: "Pennylane",  short: "Pennyl.",  cls: "bg-emerald-50 text-emerald-700 border-emerald-100", icon: "plug" },
};

const PRESETS = [
  { key: "all",   label: "Toutes dates" },
  { key: "30d",   label: "30 derniers jours" },
  { key: "90d",   label: "90 derniers jours" },
  { key: "ytd",   label: "Année en cours" },
  { key: "year",  label: "12 mois glissants" },
  { key: "custom", label: "Plage personnalisée…" },
];

function presetRange(preset) {
  const now = new Date();
  const to = now.toISOString().slice(0, 10);
  const d = new Date(now);
  if (preset === "30d") d.setDate(d.getDate() - 30);
  else if (preset === "90d") d.setDate(d.getDate() - 90);
  else if (preset === "ytd") return { from: `${now.getFullYear()}-01-01`, to };
  else if (preset === "year") d.setFullYear(d.getFullYear() - 1);
  else return { from: null, to: null };  // "all" ou "custom" → géré ailleurs
  return { from: d.toISOString().slice(0, 10), to };
}

// ---------------------------------------------------------------------------
// KPI Card — cliquable, applique un filtre pré-défini
// ---------------------------------------------------------------------------
function KpiCard({ icon: Icon, label, value, count, tone, active, onClick, testId }) {
  const tones = {
    slate:  "border-slate-200 hover:border-slate-300 bg-white",
    danger: "border-red-200 hover:border-red-300 bg-red-50/40",
    amber:  "border-amber-200 hover:border-amber-300 bg-amber-50/40",
    blue:   "border-blue-200 hover:border-blue-300 bg-blue-50/40",
  };
  const iconTone = {
    slate: "text-slate-600 bg-slate-100",
    danger: "text-red-600 bg-red-100",
    amber: "text-amber-700 bg-amber-100",
    blue: "text-blue-600 bg-blue-100",
  };
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={cn(
        "w-full text-left rounded-xl border p-4 transition-all",
        tones[tone],
        active
          ? "ring-2 ring-offset-1 ring-blue-500 shadow-sm"
          : "hover:shadow-sm"
      )}
    >
      <div className="flex items-center gap-2">
        <div className={cn("p-1.5 rounded-md", iconTone[tone])}>
          <Icon className="h-3.5 w-3.5" strokeWidth={2} />
        </div>
        <span className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">
          {label}
        </span>
      </div>
      <div className="mt-3 flex items-baseline gap-2">
        <span className="text-2xl font-semibold tracking-tight text-slate-900">
          {value}
        </span>
        {typeof count === "number" && (
          <span className="text-[11px] text-slate-500">
            {count} facture{count > 1 ? "s" : ""}
          </span>
        )}
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// PurchasesList (main)
// ---------------------------------------------------------------------------
export default function PurchasesList({ embedded = false } = {}) {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("expenses.write");
  const canValidate = hasPermission("expenses.validate");

  // --- state filters
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState([]); // multi
  const [sourceFilter, setSourceFilter] = useState([]); // multi (source_kind)
  const [supplierId, setSupplierId] = useState(null);
  const [pcgCode, setPcgCode] = useState(null);
  const [preset, setPreset] = useState("all");
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [minTtc, setMinTtc] = useState("");
  const [maxTtc, setMaxTtc] = useState("");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [duplicatesOnly, setDuplicatesOnly] = useState(false);
  const [recurringOnly, setRecurringOnly] = useState(false);
  const [pdfBrokenOnly, setPdfBrokenOnly] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // --- state table
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortBy, setSortBy] = useState("invoice_date");
  const [sortDir, setSortDir] = useState("desc");

  // --- data
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selection, setSelection] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [showBatchMarkPaid, setShowBatchMarkPaid] = useState(false);

  // Preset → date range (custom laisse dateFrom/dateTo tels quels)
  useEffect(() => {
    if (preset === "custom") { setShowAdvanced(true); return; }
    const r = presetRange(preset);
    setDateFrom(r.from);
    setDateTo(r.to);
    setPage(1);
  }, [preset]);

  const params = useMemo(() => ({
    q: q || undefined,
    status: statusFilter.length ? statusFilter.join(",") : undefined,
    source_kind: sourceFilter.length ? sourceFilter.join(",") : undefined,
    supplier_id: supplierId || undefined,
    pcg_code: pcgCode || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    min_ttc: minTtc ? Number(minTtc) : undefined,
    max_ttc: maxTtc ? Number(maxTtc) : undefined,
    overdue_only: overdueOnly || undefined,
    duplicates_only: duplicatesOnly || undefined,
    recurring_only: recurringOnly || undefined,
    pdf_broken_only: pdfBrokenOnly || undefined,
    page,
    page_size: pageSize,
    sort_by: sortBy,
    sort_dir: sortDir,
  }), [q, statusFilter, sourceFilter, supplierId, pcgCode, dateFrom, dateTo,
      minTtc, maxTtc, overdueOnly, duplicatesOnly, recurringOnly, pdfBrokenOnly,
      page, pageSize, sortBy, sortDir]);

  // Debounced fetch
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const { data: res } = await api.get("/supplier-invoices/list-enriched",
                                             { params });
        if (!cancelled) setData(res);
      } catch (e) {
        if (!cancelled) toast.error(apiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [params, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  // ── KPI click handlers (toggle filter)
  const applyKpi = (kind) => {
    setSelection(new Set()); setPage(1);
    if (kind === "outstanding") {
      const on = statusFilter.length === 2
        && statusFilter.includes("validated")
        && statusFilter.includes("partially_paid");
      setStatusFilter(on ? [] : ["validated", "partially_paid"]);
      setOverdueOnly(false); setDuplicatesOnly(false);
    } else if (kind === "overdue") {
      setOverdueOnly((v) => !v);
      setStatusFilter([]); setDuplicatesOnly(false);
    } else if (kind === "to_validate") {
      const on = statusFilter.length === 1 && statusFilter[0] === "to_validate";
      setStatusFilter(on ? [] : ["to_validate"]);
      setOverdueOnly(false); setDuplicatesOnly(false);
    } else if (kind === "due_7") {
      // approx : validated + due date <= today+7 → filtre local via badge urgent
      const on = statusFilter.length === 2
        && statusFilter.includes("validated")
        && statusFilter.includes("partially_paid")
        && overdueOnly === false;
      setStatusFilter(on ? [] : ["validated", "partially_paid"]);
      setOverdueOnly(false); setDuplicatesOnly(false);
    } else if (kind === "duplicates") {
      setDuplicatesOnly((v) => !v);
      setStatusFilter([]); setOverdueOnly(false);
    }
  };

  const kpis = data?.kpis || {};
  const rows = data?.rows || [];
  const facets = data?.facets || {};
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // ── Selection helpers
  const payableIds = rows.filter((r) => ["validated","partially_paid","imported","to_validate"].includes(r.status)).map((r) => r.id);
  const deletableIds = rows.filter((r) =>
    r.status === "to_validate" || r.status === "rejected").map((r) => r.id);
  const validatableIds = rows.filter((r) => r.status === "to_validate").map((r) => r.id);

  const toggleAll = (checked) =>
    setSelection(checked ? new Set(rows.map((r) => r.id)) : new Set());
  const toggleRow = (id, checked) => setSelection((prev) => {
    const n = new Set(prev);
    if (checked) n.add(id); else n.delete(id);
    return n;
  });

  const runDelete = async () => {
    const selectedDeletable = Array.from(selection).filter((id) => deletableIds.includes(id));
    if (!selectedDeletable.length) {
      toast.error("Aucune facture supprimable dans la sélection (statuts brouillon/refusé requis)");
      return;
    }
    if (!window.confirm(
      `Supprimer ${selectedDeletable.length} facture(s) fournisseur ? Seules les factures à valider ou refusées sont supprimables.`
    )) return;
    setBusy(true);
    try {
      const r = await api.post("/supplier-invoices/actions/batch-delete",
                                 { ids: selectedDeletable });
      toast.success(`${r.data.success.length} supprimée(s), ${r.data.failed.length} refusée(s)`);
      setSelection(new Set()); reload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const runValidate = async () => {
    const selectedValidatable = Array.from(selection).filter((id) => validatableIds.includes(id));
    if (!selectedValidatable.length) {
      toast.error("Aucune facture à valider dans la sélection");
      return;
    }
    if (!window.confirm(`Valider ${selectedValidatable.length} facture(s) fournisseur ?`)) return;
    setBusy(true);
    let ok = 0, ko = 0;
    for (const id of selectedValidatable) {
      try { await api.post(`/supplier-invoices/${id}/validate`); ok++; }
      catch { ko++; }
    }
    toast.success(`${ok} validée(s)${ko > 0 ? `, ${ko} en erreur` : ""}`);
    setSelection(new Set()); reload(); setBusy(false);
  };

  const runMarkPaid = () => {
    const selectedPayable = Array.from(selection).filter((id) => payableIds.includes(id));
    if (!selectedPayable.length) {
      toast.error("Aucune facture éligible (déjà payée ou annulée)");
      return;
    }
    setShowBatchMarkPaid(true);
  };

  const submitBatchMarkPaid = async ({ receivedAt, method }) => {
    const selectedPayable = Array.from(selection).filter((id) => payableIds.includes(id));
    if (!selectedPayable.length) {
      toast.error("Aucune facture éligible");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/supplier-invoices/actions/batch-mark-paid", {
        ids: selectedPayable,
        method,
        received_at: receivedAt,
        note: `Marquée payée en masse depuis la liste (${selectedPayable.length} factures)`,
      });
      const ok = r.data.success.length;
      const ko = r.data.failed.length;
      toast.success(`${ok} facture(s) marquée(s) payée(s)${ko > 0 ? `, ${ko} ignorée(s)` : ""}`);
      setSelection(new Set()); setShowBatchMarkPaid(false); reload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const runNormalize = async () => {
    if (!window.confirm("Nettoyer les libellés fournisseurs (trim espaces, casse, fusion doublons SIRET) ?")) return;
    setBusy(true);
    try {
      const r = await api.post("/suppliers/actions/normalize");
      toast.success(`${r.data.normalized} fournisseur(s) nettoyé(s) sur ${r.data.total_suppliers}`);
      reload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const runRename = async () => {
    if (!window.confirm("Renommer tous les PDFs fournisseurs au format Fournisseur_Date_Numero.pdf ?")) return;
    setBusy(true);
    try {
      const r = await api.post("/supplier-invoices/actions/rename-existing");
      toast.success(`${r.data.renamed} PDF renommé(s) — ${r.data.errors.length} erreur(s)`);
      reload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const resetFilters = () => {
    setQ(""); setStatusFilter([]); setSourceFilter([]); setSupplierId(null); setPcgCode(null);
    setPreset("all"); setMinTtc(""); setMaxTtc("");
    setOverdueOnly(false); setDuplicatesOnly(false); setRecurringOnly(false);
    setPage(1);
  };

  const hasActiveFilters = !!(q || statusFilter.length || sourceFilter.length || supplierId || pcgCode
                              || preset !== "all" || minTtc || maxTtc
                              || overdueOnly || duplicatesOnly || recurringOnly);

  const sortToggle = (col) => {
    if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(col); setSortDir("desc"); }
  };
  const SortIcon = ({ col }) => sortBy !== col
    ? <ChevronDown className="h-3 w-3 opacity-30" strokeWidth={2} />
    : sortDir === "asc"
      ? <ArrowUp className="h-3 w-3 text-blue-600" strokeWidth={2} />
      : <ArrowDown className="h-3 w-3 text-blue-600" strokeWidth={2} />;

  return (
    <div className="space-y-5" data-testid="purchases-page">
      {/* ── HEADER ── */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          {!embedded && (
            <>
              <div className="text-xs font-medium uppercase tracking-widest text-slate-500">
                Achats
              </div>
              <h1 className="mt-1 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900"
                  style={{ fontFamily: "Manrope, sans-serif" }}>
                Factures Fournisseurs
              </h1>
            </>
          )}
          <p className={embedded ? "text-sm text-slate-500" : "mt-1 text-sm text-slate-500"}>
            Suivi des factures d'achat, échéances, retards et détection de doublons.
          </p>
        </div>
        {canWrite && (
          <div className="flex gap-2">
            <Button variant="outline" asChild data-testid="import-batch-btn">
              <Link to="/app/purchases/import-batch">
                <Upload className="h-4 w-4 mr-1.5" strokeWidth={2} />
                Import en masse
              </Link>
            </Button>
            <Button asChild data-testid="new-supplier-invoice-btn">
              <Link to="/app/purchases/supplier-invoices/new">
                <Plus className="h-4 w-4 mr-1.5" strokeWidth={2} />
                Nouvelle facture fournisseur
              </Link>
            </Button>
          </div>
        )}
      </div>

      {/* ── KPI BAND ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          icon={Wallet} label="Encours à payer"
          value={fmtEuroCompact(kpis.outstanding?.total_ttc || 0)}
          count={kpis.outstanding?.count || 0}
          tone="slate"
          active={statusFilter.length === 2
                  && statusFilter.includes("validated")
                  && statusFilter.includes("partially_paid")
                  && !overdueOnly && !duplicatesOnly}
          onClick={() => applyKpi("outstanding")}
          testId="kpi-outstanding"
        />
        <KpiCard
          icon={AlertTriangle} label="En retard"
          value={fmtEuroCompact(kpis.overdue?.total_ttc || 0)}
          count={kpis.overdue?.count || 0}
          tone="danger"
          active={overdueOnly}
          onClick={() => applyKpi("overdue")}
          testId="kpi-overdue"
        />
        <KpiCard
          icon={Hourglass} label="À valider"
          value={fmtEuroCompact(kpis.to_validate?.total_ttc || 0)}
          count={kpis.to_validate?.count || 0}
          tone="amber"
          active={statusFilter.length === 1 && statusFilter[0] === "to_validate"}
          onClick={() => applyKpi("to_validate")}
          testId="kpi-to-validate"
        />
        <KpiCard
          icon={Clock} label="À échoir < 7j"
          value={fmtEuroCompact(kpis.due_7_days?.total_ttc || 0)}
          count={kpis.due_7_days?.count || 0}
          tone="blue"
          active={false}
          onClick={() => applyKpi("due_7")}
          testId="kpi-due-7d"
        />
      </div>

      {/* ── SOURCES WIDGET (répartition provenance) ── */}
      <SourcesWidget
        counts={data?.source_counts || {}}
        active={sourceFilter}
        onToggle={(kind) => {
          setSelection(new Set()); setPage(1);
          setSourceFilter((prev) => prev.includes(kind)
            ? prev.filter((k) => k !== kind)
            : [...prev, kind]);
        }}
      />

      {/* ── DUPLICATES ALERT ── */}
      {(kpis.duplicates?.count || 0) > 0 && !duplicatesOnly && (
        <div
          className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 text-sm text-amber-900"
          data-testid="duplicates-banner"
        >
          <div className="flex items-center gap-2">
            <Copy className="h-4 w-4" strokeWidth={2} />
            <span>
              <strong>{kpis.duplicates.count}</strong> doublon(s) potentiel(s) détecté(s)
              — même fournisseur, même montant et même mois.
            </span>
          </div>
          <Button variant="outline" size="sm" onClick={() => setDuplicatesOnly(true)}
                  data-testid="show-duplicates-btn">
            Afficher les doublons
          </Button>
        </div>
      )}

      {/* ── PDF BROKEN ALERT (Axonaut HTML error pages) ── */}
      {(kpis.pdf_broken?.count || 0) > 0 && !pdfBrokenOnly && (
        <div
          className="flex items-center justify-between gap-3 rounded-lg border border-rose-200 bg-rose-50/60 px-3 py-2 text-sm text-rose-900"
          data-testid="pdf-broken-banner"
        >
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" strokeWidth={2} />
            <span>
              <strong>{kpis.pdf_broken.count}</strong> facture(s) sans PDF exploitable
              — le fichier téléchargé depuis Axonaut est une page d'erreur HTML.
              Ré-uploadez le PDF original pour permettre l'extraction des lignes.
            </span>
          </div>
          <Button variant="outline" size="sm" onClick={() => setPdfBrokenOnly(true)}
                  data-testid="show-pdf-broken-btn">
            Afficher ces factures
          </Button>
        </div>
      )}

      {/* ── FILTERS BAR ── */}
      <Card className="border-slate-200 bg-white p-3 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input
              id="purchases-search" name="purchases-search"
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              placeholder="Recherche fournisseur, n° facture, montant, SIREN…"
              className="pl-8 h-9 text-sm"
              data-testid="purchases-search"
            />
          </div>

          <Select value={preset} onValueChange={setPreset}>
            <SelectTrigger id="filter-period" name="filter-period" className="w-[160px] h-9 text-sm" data-testid="filter-period">
              <CalendarDays className="h-3.5 w-3.5 mr-1 text-slate-500" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRESETS.map((p) => (
                <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={supplierId || "all"}
            onValueChange={(v) => { setSupplierId(v === "all" ? null : v); setPage(1); }}
          >
            <SelectTrigger id="filter-supplier" name="filter-supplier" className="w-[200px] h-9 text-sm" data-testid="filter-supplier">
              <Building2 className="h-3.5 w-3.5 mr-1 text-slate-500" />
              <SelectValue placeholder="Tous fournisseurs" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous fournisseurs</SelectItem>
              {(facets.suppliers || []).map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            variant="outline" size="sm"
            onClick={() => setShowAdvanced((v) => !v)}
            className="h-9"
            data-testid="toggle-advanced-filters"
          >
            <Filter className="h-3.5 w-3.5 mr-1" strokeWidth={2} />
            Filtres avancés
            {showAdvanced
              ? <ChevronUp className="h-3.5 w-3.5 ml-1" />
              : <ChevronDown className="h-3.5 w-3.5 ml-1" />}
          </Button>

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={resetFilters}
                    className="h-9 text-slate-500" data-testid="reset-filters-btn">
              <X className="h-3.5 w-3.5 mr-1" strokeWidth={2} />
              Réinitialiser
            </Button>
          )}
        </div>

        {showAdvanced && (
          <div className="pt-2 border-t border-slate-100 space-y-3">
            {/* Ligne 1 — Dates + Montants (4 champs chiffres alignés) */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
              <div>
                <label htmlFor="date-from" className="text-[11px] text-slate-500 flex items-center gap-1">
                  <CalendarDays className="h-3 w-3" /> Du (date)
                </label>
                <Input id="date-from" name="date-from" type="date"
                       value={dateFrom || ""}
                       onChange={(e) => { setDateFrom(e.target.value || null); setPreset("custom"); setPage(1); }}
                       className="h-9 text-sm mt-1"
                       data-testid="filter-date-from" />
              </div>
              <div>
                <label htmlFor="date-to" className="text-[11px] text-slate-500 flex items-center gap-1">
                  <CalendarDays className="h-3 w-3" /> Au (date)
                </label>
                <Input id="date-to" name="date-to" type="date"
                       value={dateTo || ""}
                       onChange={(e) => { setDateTo(e.target.value || null); setPreset("custom"); setPage(1); }}
                       className="h-9 text-sm mt-1"
                       data-testid="filter-date-to" />
              </div>
              <div>
                <label htmlFor="filter-min-ttc" className="text-[11px] text-slate-500">Montant TTC min</label>
                <Input id="filter-min-ttc" name="filter-min-ttc" type="number" value={minTtc}
                       onChange={(e) => { setMinTtc(e.target.value); setPage(1); }}
                       placeholder="0" className="h-9 text-sm mt-1"
                       data-testid="filter-min-ttc" />
              </div>
              <div>
                <label htmlFor="filter-max-ttc" className="text-[11px] text-slate-500">Montant TTC max</label>
                <Input id="filter-max-ttc" name="filter-max-ttc" type="number" value={maxTtc}
                       onChange={(e) => { setMaxTtc(e.target.value); setPage(1); }}
                       placeholder="—" className="h-9 text-sm mt-1"
                       data-testid="filter-max-ttc" />
              </div>
            </div>

            {dateFrom && !dateTo && (
              <button type="button"
                      onClick={() => { setDateTo(dateFrom); setPreset("custom"); setPage(1); }}
                      className="text-[11px] text-blue-600 hover:underline">
                → Filtrer sur cette date exacte
              </button>
            )}

            {/* Ligne 2 — Dropdowns catégorisation (3 champs alignés) */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
              <div>
                <label className="text-[11px] text-slate-500">Statut</label>
                <StatusMultiSelect value={statusFilter} onChange={setStatusFilter} />
              </div>
              <div>
                <label className="text-[11px] text-slate-500">Source</label>
                <SourceMultiSelect value={sourceFilter} onChange={setSourceFilter} />
              </div>
              <div>
                <label className="text-[11px] text-slate-500 flex items-center gap-1">
                  Catégorie comptable
                  <span title="Catégorie du Plan Comptable Général (PCG) — ex. 606100 Fournitures, 613500 Location. Auto-détectée par l'IA à l'import." className="cursor-help text-slate-400">ⓘ</span>
                </label>
                <Select
                  value={pcgCode || "all"}
                  onValueChange={(v) => { setPcgCode(v === "all" ? null : v); setPage(1); }}
                >
                  <SelectTrigger id="filter-pcg" name="filter-pcg" className="h-9 text-sm mt-1" data-testid="filter-pcg">
                    <SelectValue placeholder="Toutes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes catégories</SelectItem>
                    {(facets.pcg_codes || []).map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.code} ({c.count})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Ligne 3 — Toggles */}
            <div className="flex items-center gap-4 flex-wrap pt-1">
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <Checkbox checked={overdueOnly}
                          onCheckedChange={(c) => { setOverdueOnly(!!c); setPage(1); }}
                          data-testid="filter-overdue-only" />
                <AlertTriangle className="h-3.5 w-3.5 text-red-500" strokeWidth={2} />
                En retard uniquement
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <Checkbox checked={duplicatesOnly}
                          onCheckedChange={(c) => { setDuplicatesOnly(!!c); setPage(1); }}
                          data-testid="filter-duplicates-only" />
                <Copy className="h-3.5 w-3.5 text-amber-600" strokeWidth={2} />
                Doublons uniquement
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer" title="Fournisseurs avec ≥ 3 factures sur les 12 derniers mois — utile pour repérer les abonnements et charges fixes">
                <Checkbox checked={recurringOnly}
                          onCheckedChange={(c) => { setRecurringOnly(!!c); setPage(1); }}
                          data-testid="filter-recurring-only" />
                <Repeat className="h-3.5 w-3.5 text-indigo-600" strokeWidth={2} />
                Fournisseurs récurrents
                <span className="text-slate-400 text-xs">ⓘ</span>
              </label>
            </div>
          </div>
        )}
      </Card>

      {/* ── BATCH ACTIONS BAR ── */}
      {canWrite && (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-sm text-slate-500">
            {selection.size > 0 ? (
              <span className="text-blue-700 font-medium">
                {selection.size} sélectionnée(s)
              </span>
            ) : (
              <span>{total} facture{total > 1 ? "s" : ""}
                {hasActiveFilters ? " (filtrées)" : ""}</span>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            {selection.size > 0 && canValidate && (
              <Button size="sm" variant="outline" onClick={runValidate} disabled={busy}
                      data-testid="batch-validate-btn">
                <CheckCircle2 className="h-3.5 w-3.5 mr-1 text-blue-600" strokeWidth={2} />
                Valider sélection
              </Button>
            )}
            {selection.size > 0 && (
              <Button size="sm" variant="outline" onClick={runMarkPaid} disabled={busy}
                      title="Marque les factures sélectionnées comme intégralement payées — idéal pour les imports historiques sans rapprochement bancaire à faire."
                      className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                      data-testid="batch-mark-paid-btn">
                <Wallet className="h-3.5 w-3.5 mr-1 text-emerald-600" strokeWidth={2} />
                Marquer payées
              </Button>
            )}
            {selection.size > 0 && (
              <Button size="sm" variant="destructive" onClick={runDelete} disabled={busy}
                      data-testid="batch-delete-supplier-btn">
                <Trash2 className="h-3.5 w-3.5 mr-1" strokeWidth={2} />
                Supprimer sélection
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={runNormalize} disabled={busy}
                    title="Nettoie les libellés fournisseurs incohérents (espaces en trop, casse, doublons de nom pour un même SIRET). Utile après un gros import."
                    data-testid="normalize-suppliers-btn">
              <RefreshCw className="h-3.5 w-3.5 mr-1" strokeWidth={2} />
              Nettoyer doublons fournisseurs
            </Button>
            <Button size="sm" variant="outline" onClick={runRename} disabled={busy}
                    data-testid="rename-pdfs-btn">
              <FilePenLine className="h-3.5 w-3.5 mr-1" strokeWidth={2} />
              Renommer PDFs
            </Button>
          </div>
        </div>
      )}

      {/* ── TABLE ── */}
      <Card className="border-slate-200 rounded-xl bg-white overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-500">Chargement…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center" data-testid="empty-supplier-invoices">
            <FileText className="h-8 w-8 mx-auto text-slate-300" strokeWidth={1.5} />
            <div className="mt-3 text-sm text-slate-600">
              {hasActiveFilters
                ? "Aucune facture ne correspond à vos filtres."
                : "Aucune facture fournisseur pour l'instant."}
            </div>
            {hasActiveFilters && (
              <Button variant="link" size="sm" onClick={resetFilters}
                      className="mt-2 text-blue-600">
                Réinitialiser les filtres
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  {canWrite && (
                    <TableHead className="w-10">
                      <Checkbox
                        checked={rows.length > 0
                                 && rows.every((r) => selection.has(r.id))}
                        onCheckedChange={toggleAll}
                        data-testid="select-all-supplier-rows" />
                    </TableHead>
                  )}
                  <SortHead label="Fournisseur" col="supplier_name" sortBy={sortBy}
                            sortDir={sortDir} onSort={sortToggle} />
                  <TableHead className="text-xs">Source</TableHead>
                  <SortHead label="N°" col="invoice_number" sortBy={sortBy}
                            sortDir={sortDir} onSort={sortToggle} />
                  <SortHead label="Date" col="invoice_date" sortBy={sortBy}
                            sortDir={sortDir} onSort={sortToggle} />
                  <SortHead label="Échéance" col="due_date" sortBy={sortBy}
                            sortDir={sortDir} onSort={sortToggle} />
                  <TableHead className="text-xs">Cat. PCG</TableHead>
                  <SortHead label="HT" col="total_ht" sortBy={sortBy}
                            sortDir={sortDir} onSort={sortToggle} align="right" />
                  <TableHead className="text-xs text-right">TVA</TableHead>
                  <SortHead label="TTC" col="total_ttc" sortBy={sortBy}
                            sortDir={sortDir} onSort={sortToggle} align="right" />
                  <SortHead label="Reste dû" col="remaining_ttc" sortBy={sortBy}
                            sortDir={sortDir} onSort={sortToggle} align="right" />
                  <SortHead label="Statut" col="status" sortBy={sortBy}
                            sortDir={sortDir} onSort={sortToggle} />
                  <TableHead className="text-xs text-right w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => <Row key={r.id} r={r} canWrite={canWrite}
                                       selected={selection.has(r.id)}
                                       onToggle={toggleRow} />)}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Pagination */}
        {!loading && total > 0 && (
          <div className="flex items-center justify-between gap-3 p-3 border-t border-slate-100 text-sm">
            <div className="flex items-center gap-2 text-slate-500">
              <span>Affichage {(page - 1) * pageSize + 1}
                {" – "}
                {Math.min(page * pageSize, total)} sur {total}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Select value={String(pageSize)}
                      onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                <SelectTrigger id="page-size" name="page-size" className="w-[90px] h-8 text-xs" data-testid="page-size">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">25 / page</SelectItem>
                  <SelectItem value="50">50 / page</SelectItem>
                  <SelectItem value="100">100 / page</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      data-testid="page-prev">
                Précédent
              </Button>
              <span className="text-slate-600 text-xs px-1">
                {page} / {totalPages}
              </span>
              <Button variant="outline" size="sm" disabled={page >= totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      data-testid="page-next">
                Suivant
              </Button>
            </div>
          </div>
        )}
      </Card>

      <BatchMarkPaidDialog
        open={showBatchMarkPaid}
        onOpenChange={setShowBatchMarkPaid}
        count={Array.from(selection).filter((id) => payableIds.includes(id)).length}
        busy={busy}
        onSubmit={submitBatchMarkPaid}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// BatchMarkPaidDialog — mini-modale méthode + date pour l'action de masse.
// Utilise les mêmes valeurs de méthode que le rapprochement bancaire pour
// que les stats trésorerie par méthode restent cohérentes.
// ---------------------------------------------------------------------------
const BATCH_METHODS = [
  { value: "bank_transfer",     label: "Virement bancaire" },
  { value: "direct_debit",      label: "Prélèvement" },
  { value: "card",              label: "Carte bancaire" },
  { value: "cash",              label: "Espèces" },
  { value: "check",             label: "Chèque" },
  { value: "historical_import", label: "Import historique (méthode inconnue)" },
  { value: "other",             label: "Autre" },
];

function BatchMarkPaidDialog({ open, onOpenChange, count, busy, onSubmit }) {
  const today = new Date().toISOString().slice(0, 10);
  const [method, setMethod] = useState("bank_transfer");
  const [date, setDate] = useState(today);
  useEffect(() => {
    if (open) { setMethod("bank_transfer"); setDate(today); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const submit = () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      toast.error("Date invalide."); return;
    }
    onSubmit({ receivedAt: date, method });
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="batch-mark-paid-dialog">
        <DialogHeader>
          <DialogTitle>Marquer {count} facture{count > 1 ? "s" : ""} payée{count > 1 ? "s" : ""}</DialogTitle>
          <DialogDescription>
            Enregistre le règlement complet de chaque facture sélectionnée sans passer
            par le rapprochement bancaire. La méthode est appliquée à toutes.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label htmlFor="batch-mark-paid-method">Méthode de règlement</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger id="batch-mark-paid-method" name="batch-mark-paid-method"
                             data-testid="batch-mark-paid-method">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BATCH_METHODS.map((m) => (
                  <SelectItem key={m.value} value={m.value}
                              data-testid={`batch-mark-paid-method-${m.value}`}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="batch-mark-paid-date">Date du règlement</Label>
            <DatePickerFR value={date} onChange={setDate}
                          data-testid="batch-mark-paid-date" />
            <div className="text-xs text-slate-500 mt-1">
              Pour un import historique, tu peux mettre une date passée.
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}
                  data-testid="batch-mark-paid-cancel">
            Annuler
          </Button>
          <Button onClick={submit} disabled={busy}
                  data-testid="batch-mark-paid-submit"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white">
            {busy ? "Enregistrement…" : `Marquer ${count} payée${count > 1 ? "s" : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Row (dense line)
// ---------------------------------------------------------------------------
function Row({ r, canWrite, selected, onToggle }) {
  const remaining = r.remaining_ttc || 0;
  const dueDelta = daysBetween(r.due_date);
  const dueBadge = (() => {
    if (!r.due_date || r.status === "paid") return null;
    if (r.is_late) return (
      <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-red-100 text-red-700 border border-red-200">
        Retard {r.days_overdue}j
      </span>
    );
    if (dueDelta != null && dueDelta <= 7 && remaining > 0.01) return (
      <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
        J–{dueDelta}
      </span>
    );
    return null;
  })();

  const meta = STATUS_META[r.status] || { label: r.status, cls: "bg-slate-100 text-slate-700" };
  const payMeta = paymentMeta(r);

  return (
      <TableRow className="hover:bg-slate-50/70 transition-colors cursor-pointer"
              onClick={(e) => {
                if (e.target.closest("button, a, input, label, [role='dialog']")) return;
                window.location.assign(`/app/purchases/supplier-invoices/${r.id}`);
              }}
              data-testid={`row-supplier-invoice-${r.id}`}>
      {canWrite && (
        <TableCell className="w-10">
          <Checkbox checked={selected}
                    onCheckedChange={(c) => onToggle(r.id, c)}
                    data-testid={`select-supplier-${r.id}`} />
        </TableCell>
      )}
      <TableCell>
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-7 h-7 rounded-md bg-slate-100 flex items-center justify-center text-[10px] font-semibold text-slate-600 shrink-0">
            {(r.supplier_name || "?").slice(0, 2).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="font-medium text-slate-900 truncate max-w-[180px]"
                 title={r.supplier_name}>
              {r.supplier_name || "—"}
            </div>
            <div className="flex items-center gap-1 mt-0.5">
              {r.is_recurrent && (
                <Popover>
                  <PopoverTrigger asChild>
                    <span className="inline-flex items-center gap-0.5 text-[9px] font-medium px-1 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-100 cursor-help">
                      <Repeat className="h-2.5 w-2.5" strokeWidth={2.5} />
                      Récurrent
                    </span>
                  </PopoverTrigger>
                  <PopoverContent className="text-xs w-52">
                    Fournisseur récurrent (≥ 3 factures sur les 12 derniers mois)
                  </PopoverContent>
                </Popover>
              )}
              {r.concentration_pct > 30 && (
                <Popover>
                  <PopoverTrigger asChild>
                    <span className="inline-flex items-center gap-0.5 text-[9px] font-medium px-1 py-0.5 rounded bg-red-50 text-red-700 border border-red-100 cursor-help"
                          data-testid={`row-concentration-badge-${r.id}`}>
                      <AlertTriangle className="h-2.5 w-2.5" strokeWidth={2.5} />
                      Concentration {Math.round(r.concentration_pct)}%
                    </span>
                  </PopoverTrigger>
                  <PopoverContent className="text-xs w-64">
                    <div className="font-medium text-slate-900 mb-1">
                      Concentration fournisseur
                    </div>
                    <div className="text-slate-600">
                      Ce fournisseur représente <b>{r.concentration_pct.toFixed(1)}%</b> du
                      total de vos achats sur les 12 derniers mois.
                      {r.concentration_pct >= 80
                        ? " Forte dépendance — envisagez de diversifier vos fournisseurs pour réduire le risque."
                        : " Un poids élevé peut fragiliser votre chaîne d'approvisionnement."}
                    </div>
                  </PopoverContent>
                </Popover>
              )}
              {r.is_duplicate_candidate && (
                <Popover>
                  <PopoverTrigger asChild>
                    <span className="inline-flex items-center gap-0.5 text-[9px] font-medium px-1 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-100 cursor-help">
                      <Copy className="h-2.5 w-2.5" strokeWidth={2.5} />
                      Doublon ?
                    </span>
                  </PopoverTrigger>
                  <PopoverContent className="text-xs w-52">
                    Une autre facture de ce fournisseur avec le même montant existe le
                    même mois — à vérifier.
                  </PopoverContent>
                </Popover>
              )}
              {r.pdf_broken && (
                <Popover>
                  <PopoverTrigger asChild>
                    <span className="inline-flex items-center gap-0.5 text-[9px] font-medium px-1 py-0.5 rounded bg-rose-50 text-rose-800 border border-rose-100 cursor-help"
                          data-testid={`row-pdf-broken-badge-${r.id}`}>
                      <AlertTriangle className="h-2.5 w-2.5" strokeWidth={2.5} />
                      PDF cassé
                    </span>
                  </PopoverTrigger>
                  <PopoverContent className="text-xs w-64">
                    <div className="font-medium text-slate-900 mb-1">
                      PDF non exploitable
                    </div>
                    <div className="text-slate-600">
                      Le fichier téléchargé depuis Axonaut est une page HTML d'erreur,
                      pas un vrai PDF. Ré-uploadez le PDF original pour extraire les
                      lignes détail.
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
          </div>
        </div>
      </TableCell>
      <TableCell><SourceBadge kind={r.source_kind} /></TableCell>
      <TableCell className="text-slate-700 text-sm">{r.invoice_number || "—"}</TableCell>
      <TableCell className="text-slate-600 text-sm">{fmtDate(r.invoice_date)}</TableCell>
      <TableCell>
        <div className="flex items-center gap-1.5 text-sm text-slate-600">
          <span>{fmtDate(r.due_date)}</span>
          {dueBadge}
        </div>
      </TableCell>
      <TableCell className="text-[11px] text-slate-500 font-mono">
        {r.pcg_code || "—"}
      </TableCell>
      <TableCell className="text-right text-sm text-slate-700">
        {fmtEuro(r.total_ht)}
      </TableCell>
      <TableCell className="text-right text-sm text-slate-500">
        {fmtEuro(r.total_vat)}
      </TableCell>
      <TableCell className="text-right text-sm text-slate-900 font-medium">
        {fmtEuro(r.total_ttc)}
      </TableCell>
      <TableCell className="text-right text-sm">
        {remaining > 0.01 ? (
          <span className={cn("font-medium",
                              r.is_late ? "text-red-700" : "text-slate-800")}>
            {fmtEuro(remaining)}
          </span>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </TableCell>
      <TableCell>
        <div className="flex flex-col items-start gap-1">
          <Badge variant="outline" className={cn("text-[10px] font-medium", meta.cls)}>
            {meta.label}
          </Badge>
          {payMeta && (
            <Badge variant="outline"
                   className={cn("text-[10px] font-medium", payMeta.cls)}
                   data-testid={`row-payment-badge-${r.id}`}>
              {payMeta.label}
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-0.5">
          <PdfPreviewPopover fileAssetId={r.file_asset_id}
                              testId={`purchase-pdf-${r.id}`} />
          <Button variant="ghost" size="sm" asChild
                  className="h-7 w-7 p-0">
            <Link to={`/app/purchases/supplier-invoices/${r.id}`}
                  data-testid={`open-supplier-${r.id}`}>
              <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2} />
            </Link>
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// SortHead (colonne triable)
// ---------------------------------------------------------------------------
function SortHead({ label, col, sortBy, sortDir, onSort, align = "left" }) {
  const active = sortBy === col;
  return (
    <TableHead className={cn("text-xs cursor-pointer select-none",
                             align === "right" && "text-right")}
               onClick={() => onSort(col)}>
      <div className={cn("inline-flex items-center gap-1",
                         align === "right" && "flex-row-reverse")}>
        <span className={active ? "text-slate-900" : ""}>{label}</span>
        {active
          ? sortDir === "asc"
            ? <ArrowUp className="h-3 w-3 text-blue-600" strokeWidth={2.5} />
            : <ArrowDown className="h-3 w-3 text-blue-600" strokeWidth={2.5} />
          : <ChevronDown className="h-3 w-3 opacity-30" strokeWidth={2} />}
      </div>
    </TableHead>
  );
}

// ---------------------------------------------------------------------------
// StatusMultiSelect
// ---------------------------------------------------------------------------
function StatusMultiSelect({ value, onChange }) {
  const opts = [
    { key: "to_validate", label: "À valider" },
    { key: "validated", label: "Validée" },
    { key: "imported", label: "Importée" },
    { key: "partially_paid", label: "Part. payée" },
    { key: "paid", label: "Payée" },
    { key: "rejected", label: "Refusée" },
  ];
  const toggle = (k) => {
    const set = new Set(value);
    if (set.has(k)) set.delete(k); else set.add(k);
    onChange(Array.from(set));
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline"
                className="h-9 mt-1 w-full justify-between text-sm font-normal"
                data-testid="filter-status">
          <span className="truncate">
            {value.length
              ? `${value.length} statut${value.length > 1 ? "s" : ""}`
              : "Tous statuts"}
          </span>
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="start">
        {opts.map((o) => (
          <label key={o.key}
                 className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-slate-50 cursor-pointer text-sm">
            <Checkbox checked={value.includes(o.key)}
                      onCheckedChange={() => toggle(o.key)}
                      data-testid={`status-opt-${o.key}`} />
            {o.label}
          </label>
        ))}
        {value.length > 0 && (
          <Button variant="ghost" size="sm" onClick={() => onChange([])}
                  className="w-full mt-1 text-xs text-slate-500">
            Effacer
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
}


// ---------------------------------------------------------------------------
// SourceBadge — badge coloré indiquant l'origine (Manuel / OCR / Axonaut / …)
// ---------------------------------------------------------------------------
function SourceBadge({ kind }) {
  const meta = SOURCE_META[kind] || { label: kind || "Manuel", short: "?", cls: "bg-slate-50 text-slate-600 border-slate-200", icon: "user" };
  const Icon = kind === "axonaut" || kind === "pennylane"
    ? Plug
    : kind === "ocr" || kind === "import_batch"
      ? ScanLine
      : kind === "recurrence"
        ? Repeat
        : User;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <span
          className={cn("inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border cursor-help whitespace-nowrap",
                        meta.cls)}
          data-testid={`source-badge-${kind}`}
        >
          <Icon className="h-2.5 w-2.5" strokeWidth={2.5} />
          {meta.short}
        </span>
      </PopoverTrigger>
      <PopoverContent className="text-xs w-56">
        <div className="font-medium mb-1">{meta.label}</div>
        {kind === "manual" && "Facture saisie manuellement via le formulaire."}
        {kind === "ocr" && "Facture importée par upload PDF avec extraction OCR IA (Gemini)."}
        {kind === "import_batch" && "Facture importée via l'import en masse (plusieurs PDFs à la fois)."}
        {kind === "recurrence" && "Facture générée automatiquement par une règle de récurrence."}
        {kind === "axonaut" && "Facture synchronisée depuis Axonaut (connecteur comptable)."}
        {kind === "pennylane" && "Facture synchronisée depuis Pennylane (connecteur comptable)."}
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// SourcesWidget — chips cliquables affichant la répartition par provenance.
// ---------------------------------------------------------------------------
function SourcesWidget({ counts, active, onToggle }) {
  const entries = [
    { key: "manual",       label: "Manuelles",       icon: User },
    { key: "ocr",          label: "OCR IA",          icon: ScanLine },
    { key: "import_batch", label: "Import en lot",   icon: Upload },
    { key: "recurrence",   label: "Récurrentes",     icon: Repeat },
    { key: "axonaut",      label: "Axonaut",         icon: Plug },
    { key: "pennylane",    label: "Pennylane",       icon: Plug },
  ].filter((e) => (counts[e.key] || 0) > 0);

  if (!entries.length) return null;
  const total = entries.reduce((s, e) => s + (counts[e.key] || 0), 0);

  return (
    <Card className="border-slate-200 bg-white p-3" data-testid="sources-widget">
      <div className="flex items-center gap-2 mb-2">
        <Layers className="h-3.5 w-3.5 text-slate-500" strokeWidth={2} />
        <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
          Provenance des factures — {total} au total
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        {entries.map((e) => {
          const isActive = active.includes(e.key);
          const meta = SOURCE_META[e.key] || {};
          const Icon = e.icon;
          return (
            <button
              key={e.key}
              onClick={() => onToggle(e.key)}
              data-testid={`sources-chip-${e.key}`}
              className={cn(
                "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs transition-all",
                isActive
                  ? cn(meta.cls, "ring-2 ring-offset-1 ring-blue-500 font-medium")
                  : cn(meta.cls, "hover:shadow-sm")
              )}
            >
              <Icon className="h-3 w-3" strokeWidth={2} />
              <span>{e.label}</span>
              <span className="tabular-nums font-semibold">{counts[e.key]}</span>
            </button>
          );
        })}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// SourceMultiSelect — filtre multi-sources dans les filtres avancés.
// ---------------------------------------------------------------------------
function SourceMultiSelect({ value, onChange }) {
  const opts = [
    { key: "manual", label: "Manuel" },
    { key: "ocr", label: "OCR IA" },
    { key: "import_batch", label: "Import en lot" },
    { key: "recurrence", label: "Récurrence" },
    { key: "axonaut", label: "Axonaut" },
    { key: "pennylane", label: "Pennylane" },
  ];
  const toggle = (k) => {
    const set = new Set(value);
    if (set.has(k)) set.delete(k); else set.add(k);
    onChange(Array.from(set));
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline"
                className="h-9 mt-1 w-full justify-between text-sm font-normal"
                data-testid="filter-source">
          <span className="truncate">
            {value.length
              ? `${value.length} source${value.length > 1 ? "s" : ""}`
              : "Toutes sources"}
          </span>
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="start">
        {opts.map((o) => (
          <label key={o.key}
                 className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-slate-50 cursor-pointer text-sm">
            <Checkbox checked={value.includes(o.key)}
                      onCheckedChange={() => toggle(o.key)}
                      data-testid={`source-opt-${o.key}`} />
            {o.label}
          </label>
        ))}
        {value.length > 0 && (
          <Button variant="ghost" size="sm" onClick={() => onChange([])}
                  className="w-full mt-1 text-xs text-slate-500">
            Effacer
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
}
